import React, { useContext, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Wand2, Loader2, X } from 'lucide-react';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';

/**
 * MarkdownAIToolbar — a compact "Generate with AI" + "Improve"
 * strip that appears above ANY plain-text / markdown textarea. Same
 * modal UX as the RichTextEditor inline modal so admins get parity
 * across section types.
 *
 * Props:
 *   value        current markdown/text content
 *   onChange     setter for the parent's content field
 *   aiContext    { lessonId } — passed to /admin/ai/section-content
 *   sectionType  'markdown' | 'notes' | 'text' — informs the backend prompt
 *
 * Design notes:
 *   • Uses the SAME endpoint the RichTextEditor uses (/admin/ai/section-content)
 *     but the backend returns { html: string } for section_type=rich_text.
 *     For markdown we explicitly send section_type='rich_text' then convert
 *     the returned HTML to lightweight markdown (h2 -> ##, li -> -, strong ->
 *     **, etc.) so the textarea stays readable.
 */
const htmlToMarkdown = (html) => {
  if (!html) return '';
  let s = String(html);
  // Line-break blocks first so we don't collapse structure later.
  s = s.replace(/<h1[^>]*>/gi, '\n# ').replace(/<\/h1>/gi, '\n');
  s = s.replace(/<h2[^>]*>/gi, '\n## ').replace(/<\/h2>/gi, '\n');
  s = s.replace(/<h3[^>]*>/gi, '\n### ').replace(/<\/h3>/gi, '\n');
  s = s.replace(/<h4[^>]*>/gi, '\n#### ').replace(/<\/h4>/gi, '\n');
  s = s.replace(/<p[^>]*>/gi, '\n').replace(/<\/p>/gi, '\n');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<li[^>]*>/gi, '- ').replace(/<\/li>/gi, '\n');
  s = s.replace(/<\/?(?:ul|ol)[^>]*>/gi, '\n');
  s = s.replace(/<strong[^>]*>|<b[^>]*>/gi, '**').replace(/<\/strong>|<\/b>/gi, '**');
  s = s.replace(/<em[^>]*>|<i[^>]*>/gi, '_').replace(/<\/em>|<\/i>/gi, '_');
  s = s.replace(/<a[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi, '[$2]($1)');
  s = s.replace(/<[^>]+>/g, '');   // strip any remaining tags
  s = s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
  s = s.replace(/\n{3,}/g, '\n\n').trim();
  return s;
};

const MarkdownAIToolbar = ({ value, onChange, aiContext, sectionType = 'markdown' }) => {
  const { API } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [running, setRunning] = useState(false);
  const textareaRef = useRef(null);

  const openModal = (m) => {
    setMode(m);
    setOpen(true);
  };

  const run = async () => {
    const p = (prompt || '').trim();
    if (!p) { toast.error('Enter a prompt first.'); return; }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai/section-content`, {
        lesson_id: aiContext?.lessonId || null,
        section_type: 'rich_text',
        prompt: p,
        existing_content: mode === 'improve' ? (value || '') : null,
      });
      const html = data?.content?.html || '';
      const md = htmlToMarkdown(html);
      if (!md) { toast.error('Model returned no content.'); return; }
      if (mode === 'improve') {
        onChange(md);
      } else {
        const glue = (value && !value.endsWith('\n')) ? '\n\n' : '';
        onChange(`${value || ''}${glue}${md}`);
      }
      toast.success(mode === 'improve' ? 'Section improved.' : 'AI content added.');
      setOpen(false);
      setPrompt('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed.');
    } finally { setRunning(false); }
  };

  return (
    <>
      <div className="flex items-center gap-1.5" data-testid="markdown-ai-toolbar">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => openModal('generate')}
          className="h-7 px-2 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/10 font-semibold"
          data-testid="markdown-ai-generate-btn"
        >
          <Sparkles className="w-3.5 h-3.5" /> AI
        </Button>
        {(value || '').trim() && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => openModal('improve')}
            className="h-7 px-2 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/10 font-semibold"
            data-testid="markdown-ai-improve-btn"
          >
            <Wand2 className="w-3.5 h-3.5" /> Improve
          </Button>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur-sm"
          onClick={() => !running && setOpen(false)}
          data-testid="markdown-ai-modal"
        >
          <div
            className="bg-background border border-border rounded-xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">AI assist · Markdown</div>
                <h3 className="text-base font-bold mt-1 flex items-center gap-1.5">
                  {mode === 'improve' ? <Wand2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                  {mode === 'improve' ? 'Improve this markdown' : 'Generate markdown'}
                </h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {mode === 'improve'
                    ? 'Rewrite / expand / shorten the current markdown.'
                    : 'Result is appended to the end of the field.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !running && setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="markdown-ai-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder={mode === 'improve'
                  ? 'e.g. "make the bullets shorter, add a small closing summary"'
                  : 'e.g. "step-by-step markdown checklist for cleaning a Harvest Right vacuum pump"'}
                className="w-full text-sm p-3 rounded-lg border border-input bg-background font-mono"
                data-testid="markdown-ai-prompt"
                autoFocus
              />
              {(value || '').trim() && (
                <div className="inline-flex rounded-full border border-border overflow-hidden text-xs" data-testid="markdown-ai-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setMode('generate')}
                    className={`px-3 py-1 font-semibold ${mode === 'generate' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
                  >
                    Append
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('improve')}
                    className={`px-3 py-1 font-semibold ${mode === 'improve' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
                  >
                    Replace (improve)
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={running}
                  className="flex-1"
                  data-testid="markdown-ai-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={run}
                  disabled={running || !prompt.trim()}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="markdown-ai-run"
                >
                  {running
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</>
                    : <><Sparkles className="w-4 h-4 mr-1.5" /> {mode === 'improve' ? 'Improve' : 'Generate'}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarkdownAIToolbar;
